import React, { useEffect, useReducer, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import consumer from "../channels/consumer"
import { reduceToolCards, parseArgs } from "./toolCallReducer"
import {
  observeReducer,
  initialObserveState,
  durableEvents,
} from "./observeReducer"

// Read-only observe client for an agent Session (foundation slices 3 + 4 + 5).
//
// Subscribes to SessionChannel (Warden cookie auth happens at the cable Connection layer). The
// REPLAY HANDSHAKE (slice 5, the determinism contract):
//   1. Subscribe and BUFFER live frames first (so nothing that arrives during the round-trip is lost).
//   2. THEN request the durable backlog via `replay` (seq > last_seq we already hold).
//   3. Flush buffered live frames AFTER the backlog, through the SAME pure reducer; the reducer
//      dedupes by seq, so a live frame already covered by the backlog is a no-op, and ordering does
//      not matter (settled state is rebuilt from the seq map — replay determinism, AC1).
//   4. GAP RESYNC (AC2): if the reducer reports `needsResync` (an arriving durable seq jumped past
//      the contiguous applied prefix), STOP and re-request the backlog from `resyncFrom`
//      (= lastAppliedSeq). Dropping the socket mid-stream is just the same handshake on reconnect.
//
// Two render lanes:
//   - DURABLE events (`kind: 'event'` / `replay_batch`) — canonical, deduped by seq.
//   - EPHEMERAL token deltas (`kind: 'delta'`, slice 4) — drive a LIVE TYPEWRITER per messageId.
// All frame-handling logic lives in the pure observeReducer, which is unit-assertable off the wire.
//
// THIS FILE IS PURE PAINT over that contract: the reducers (observeReducer.js + toolCallReducer.js)
// are untouched — only the JSX render + the inline `styles` object below changed (demo polish,
// cribbed from MetaAgentChatPage). The raw seq/type/json envelope list is preserved verbatim,
// just moved into a collapsed "Debug" accordion.

// ── Styles (system-font, inline, no new deps — same shape as MetaAgentChatPage) ──────────────────
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    maxWidth: 860,
    margin: "0 auto",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: "#111",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 24px",
    borderBottom: "1px solid #e5e5e5",
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 2,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  brandMark: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: "linear-gradient(135deg, #6d28d9, #4f46e5)",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandTitle: { fontSize: 16, fontWeight: 700, margin: 0, lineHeight: 1.1 },
  brandSub: { fontSize: 12, color: "#888", margin: 0 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  pillDot: { width: 8, height: 8, borderRadius: "50%" },
  toolbar: {
    display: "flex",
    gap: 8,
    padding: "10px 24px",
    borderBottom: "1px solid #f0f0f0",
    alignItems: "center",
  },
  toolbarBtn: {
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid #d0d0d0",
    backgroundColor: "#fff",
    color: "#333",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
  toolbarNote: { fontSize: 12, color: "#999", marginLeft: "auto" },
  messages: {
    flex: 1,
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  messageBubble: {
    alignSelf: "flex-start",
    maxWidth: "88%",
    padding: "12px 16px",
    borderRadius: 12,
    borderBottomLeftRadius: 4,
    backgroundColor: "#f5f5f5",
    color: "#111",
    fontSize: 14,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  liveBubble: {
    backgroundColor: "#f0f7ff",
    border: "1px solid #d8e8ff",
  },
  toolCallCard: {
    alignSelf: "flex-start",
    maxWidth: "88%",
    width: "100%",
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    overflow: "hidden",
    fontSize: 13,
    backgroundColor: "#fff",
  },
  toolCallHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    backgroundColor: "#fafafa",
    gap: 8,
  },
  toolCallName: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontWeight: 600,
    color: "#333",
  },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "2px 8px",
    borderRadius: 999,
  },
  toolCallSection: {
    padding: "8px 14px",
    borderTop: "1px solid #eee",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "#444",
  },
  toolCallLabel: { color: "#999", fontWeight: 600, marginRight: 6 },
  emptyState: {
    flex: 1,
    minHeight: 240,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    fontSize: 15,
    textAlign: "center",
    padding: 40,
  },
  rejectedBanner: {
    margin: "24px",
    padding: "18px 20px",
    borderRadius: 12,
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  cursor: { opacity: 0.5, marginLeft: 1 },
  debug: {
    margin: "0 24px 32px",
    border: "1px solid #eee",
    borderRadius: 10,
    overflow: "hidden",
  },
  debugSummary: {
    cursor: "pointer",
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: "#666",
    backgroundColor: "#fafafa",
    userSelect: "none",
  },
  debugList: {
    margin: 0,
    padding: "8px 14px 14px 28px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11.5,
    color: "#555",
    lineHeight: 1.7,
    listStyle: "decimal",
  },
}

// Status pill colour + label per connection state.
const pillFor = (status, rejected, running) => {
  if (rejected || status === "rejected") return { bg: "#fef2f2", fg: "#b91c1c", dot: "#dc2626", label: "rejected" }
  if (status === "connecting") return { bg: "#fffbeb", fg: "#b45309", dot: "#f59e0b", label: "connecting" }
  if (status === "disconnected") return { bg: "#f3f4f6", fg: "#6b7280", dot: "#9ca3af", label: "disconnected" }
  if (running) return { bg: "#ecfdf5", fg: "#047857", dot: "#10b981", label: "running" }
  return { bg: "#eef2ff", fg: "#4338ca", dot: "#6366f1", label: "done" }
}

const badgeColor = (status) => (status === "complete" ? "#10b981" : "#f59e0b")

// Fold the durable stream into ordered render rows: assistant message bubbles (durable
// TEXT_MESSAGE_CONTENT coalesced per messageId) interleaved with tool-call cards, by first-seq.
// Pure derivation off the reducer's settled durable list — adds NO logic to the reducer.
const buildTimeline = (events, toolCards) => {
  const messages = {} // messageId -> { messageId, text, firstSeq }
  for (const e of events) {
    if (e.type !== "TEXT_MESSAGE_CONTENT") continue
    const p = e.payload || {}
    const mid = p.messageId
    if (mid == null) continue
    const chunk = p.delta != null ? p.delta : p.content != null ? p.content : ""
    if (!messages[mid]) messages[mid] = { messageId: mid, text: "", firstSeq: e.seq }
    messages[mid].text += chunk
  }
  const rows = []
  Object.values(messages).forEach((m) =>
    rows.push({ kind: "message", sortSeq: m.firstSeq, ...m })
  )
  toolCards.forEach((c) =>
    rows.push({ kind: "tool", sortSeq: c.startSeq || 0, card: c })
  )
  return rows.sort((a, b) => a.sortSeq - b.sortSeq)
}

const RUN_FINISHED = "RUN_FINISHED"

const ObserveSessionPage = () => {
  const { id } = useParams()
  const [state, dispatch] = useReducer(observeReducer, undefined, initialObserveState)
  const [status, setStatus] = useState("connecting")
  const [rejected, setRejected] = useState(false)
  const [showDebug, setShowDebug] = useState(false)
  const subRef = useRef(null)

  // The handshake needs the latest bookkeeping synchronously on (re)connect; mirror it in refs.
  const maxSeqRef = useRef(0)
  const lastAppliedRef = useRef(0)
  const needsResyncRef = useRef(false)
  const resyncFromRef = useRef(0)
  maxSeqRef.current = state.maxSeq
  lastAppliedRef.current = state.lastAppliedSeq
  needsResyncRef.current = state.needsResync
  resyncFromRef.current = state.resyncFrom

  // Live frames that arrive before the backlog has been requested are buffered, then flushed AFTER
  // the backlog so the handshake is deterministic. The flag flips once the backlog is requested.
  const liveBufferRef = useRef([])
  const handshakeDoneRef = useRef(false)

  // Step 4: whenever the reducer reports a gap, re-request the backlog from the contiguous prefix.
  useEffect(() => {
    if (state.needsResync && subRef.current) {
      subRef.current.perform("replay", { last_seq: state.resyncFrom })
    }
  }, [state.needsResync, state.resyncFrom])

  useEffect(() => {
    const flushLiveBuffer = () => {
      const buffered = liveBufferRef.current
      liveBufferRef.current = []
      buffered.forEach((frame) => dispatch(frame))
    }

    const subscription = consumer.subscriptions.create(
      { channel: "SessionChannel", id },
      {
        connected() {
          setStatus("connected")
          // Reset the handshake on every (re)connect: buffer live first, then request the backlog
          // from what we already hold (gap-fill). 0 on a truly fresh open.
          handshakeDoneRef.current = false
          liveBufferRef.current = []
          const lastSeq = needsResyncRef.current ? resyncFromRef.current : maxSeqRef.current
          subscription.perform("replay", { last_seq: lastSeq })
          handshakeDoneRef.current = true
          flushLiveBuffer()
        },
        disconnected() {
          setStatus("disconnected")
        },
        rejected() {
          setRejected(true)
          setStatus("rejected")
        },
        received(data) {
          // Buffer live durable/delta frames that land before the backlog request completes; the
          // backlog (replay_batch) is applied immediately. After the handshake, everything flows
          // straight through. The reducer dedupes by seq, so a double-delivered seq is harmless.
          if (data && data.kind === "replay_batch") {
            dispatch(data)
            return
          }
          if (!handshakeDoneRef.current) {
            liveBufferRef.current.push(data)
            return
          }
          dispatch(data)
        },
      }
    )
    subRef.current = subscription
    return () => subscription.unsubscribe()
  }, [id])

  // Rebuild settled state from scratch (proves replay backlog completeness — durable only).
  const replayFromZero = () => subRef.current && subRef.current.perform("replay", { last_seq: 0 })
  // Simulate kill + reconnect: drop state and re-subscribe, which replays and rebuilds.
  const reconnect = () => window.location.reload()

  const events = durableEvents(state)
  const liveMessages = Object.entries(state.liveDeltas) // [messageId, livingText]
  // Fold the durable stream into tool-call cards (slice 7). Pure reducer (see
  // toolCallReducer.js) so live-order and replay-order settle to the same cards.
  const toolCards = reduceToolCards(events)
  const timeline = buildTimeline(events, toolCards)

  const runFinished = events.some((e) => e.type === RUN_FINISHED)
  const running = !runFinished && (status === "connected" || liveMessages.length > 0)
  const pill = pillFor(status, rejected, running)
  const hasContent = timeline.length > 0 || liveMessages.length > 0

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.brandMark}>V</div>
          <div>
            <p style={styles.brandTitle}>Vesta AI Agent</p>
            <p style={styles.brandSub}>Session #{id} · Agent View (read-only)</p>
          </div>
        </div>
        <span style={{ ...styles.pill, backgroundColor: pill.bg, color: pill.fg }}>
          <span style={{ ...styles.pillDot, backgroundColor: pill.dot }} />
          {pill.label}
        </span>
      </div>

      <div style={styles.toolbar}>
        <button style={styles.toolbarBtn} onClick={replayFromZero}>
          ↺ Replay from 0
        </button>
        <button style={styles.toolbarBtn} onClick={reconnect}>
          ⟳ Kill + reconnect
        </button>
        <span style={styles.toolbarNote}>
          {events.length} durable events · applied through seq {state.lastAppliedSeq}
          {state.needsResync && ` · resyncing from ${state.resyncFrom}`}
        </span>
      </div>

      {rejected ? (
        <div style={styles.rejectedBanner}>
          <strong>Subscription rejected by the tenant gate.</strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            This session belongs to another company. The SessionChannel fail-closed guard refused the
            stream — no events are delivered across tenants.
          </div>
        </div>
      ) : !hasContent ? (
        <div style={styles.emptyState}>
          <div>
            <div style={{ fontSize: 22, marginBottom: 10 }}>Waiting for the agent…</div>
            <div style={{ fontSize: 14 }}>
              The live session view will fill in as durable events and token deltas arrive.
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.messages}>
          {timeline.map((row) =>
            row.kind === "message" ? (
              <div key={`msg-${row.messageId}`} style={styles.messageBubble}>
                {row.text}
              </div>
            ) : (
              <div key={`tool-${row.card.toolCallId}`} style={styles.toolCallCard}>
                <div style={styles.toolCallHeader}>
                  <span style={styles.toolCallName}>🔧 {row.card.name || "tool"}</span>
                  <span style={{ ...styles.badge, backgroundColor: badgeColor(row.card.status) }}>
                    {row.card.status === "complete" ? "complete" : "running"}
                  </span>
                </div>
                <div style={styles.toolCallSection}>
                  <span style={styles.toolCallLabel}>args</span>
                  {JSON.stringify(parseArgs(row.card))}
                </div>
                {row.card.result != null && (
                  <div style={styles.toolCallSection}>
                    <span style={styles.toolCallLabel}>result</span>
                    {row.card.result}
                  </div>
                )}
              </div>
            )
          )}

          {/* Live typewriter — ephemeral deltas not yet reconciled to a durable message. */}
          {liveMessages.map(([mid, text]) => (
            <div key={`live-${mid}`} style={{ ...styles.messageBubble, ...styles.liveBubble }}>
              {text}
              <span style={styles.cursor}>▋</span>
            </div>
          ))}
        </div>
      )}

      {/* Raw envelope list preserved, collapsed (useful for QA, not front-and-centre). */}
      <details style={styles.debug} open={showDebug} onToggle={(e) => setShowDebug(e.target.open)}>
        <summary style={styles.debugSummary}>
          Debug · raw durable envelopes ({events.length})
        </summary>
        <ol style={styles.debugList}>
          {events.map((e) => (
            <li key={e.seq}>
              #{e.seq} <strong>{e.type}</strong> {JSON.stringify(e.payload)}
            </li>
          ))}
        </ol>
      </details>
    </div>
  )
}

export default ObserveSessionPage
